import { useEffect, useMemo } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useVoiceTTS } from '@/hooks/useVoiceProviders';
import { VoiceIntegration } from '@/shared/types/integration';
import { TTSProvider } from '@/shared/types/voiceProviders';

import CartesiaVoice from './CartesiaVoice';
import ElevenLabsVoice from './ElevenLabsVoice';
import MiniMaxVoice from './MiniMaxVoice';
import { TTSModelSelector } from './TTSModelSelector';
import CardBase from '../../Components/CardBase';
import CardSkeleton from '../../Components/CardSkeleton';

const Voice = () => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<VoiceIntegration>();

  const ttsProvider = useWatch({ control, name: 'config.tts_provider' }) || '';
  const elevenlabsLang = useWatch({ control, name: 'config.tts_elevenlabs.language_code' });
  const cartesiaLang = useWatch({ control, name: 'config.tts_cartesia.language_code' });
  const elevenlabsModelId = useWatch({ control, name: 'config.tts_elevenlabs.model_id' }) || '';
  const cartesiaModelId = useWatch({ control, name: 'config.tts_cartesia.model_id' }) || '';
  const minimaxModelId = useWatch({ control, name: 'config.tts_minimax.model_id' }) || '';

  const { models, getVoicesByProvider, isLoading } = useVoiceTTS();

  const modelIdByProvider: Partial<Record<TTSProvider, string>> = {
    elevenlabs: elevenlabsModelId,
    cartesia: cartesiaModelId,
    minimax: minimaxModelId,
  };
  const currentModelId = modelIdByProvider[ttsProvider as TTSProvider] ?? '';

  // Hide models whose provider has no voices for the selected language
  // (e.g. ElevenLabs voices are mostly "en", so picking Chinese leaves only Cartesia).
  // Fallback: if no model is viable, return the full list to avoid an empty dropdown
  // during edge cases (e.g. voices still loading).
  const filteredModels = useMemo(() => {
    if (!models || models.length === 0) {
      return models ?? [];
    }
    const langByProvider: Record<TTSProvider, string | undefined> = {
      elevenlabs: elevenlabsLang,
      cartesia: cartesiaLang,
      // MiniMax voices are multilingual (no per-voice language); language_boost is a
      // synthesis hint, not a filter — so never narrow models by it.
      minimax: undefined,
    };
    const viable = models.filter(
      (model) => getVoicesByProvider(model.provider, langByProvider[model.provider]).length > 0
    );
    return viable.length > 0 ? viable : models;
  }, [models, elevenlabsLang, cartesiaLang, getVoicesByProvider]);

  const applyModel = (modelId: string, provider: TTSProvider) => {
    setValue('config.tts_provider', provider, { shouldDirty: true });
    if (provider === 'elevenlabs') {
      setValue('config.tts_elevenlabs.model_id', modelId, { shouldDirty: true });
      setValue('config.tts_cartesia.model_id', '', { shouldDirty: true });
      setValue('config.tts_minimax.model_id', undefined, { shouldDirty: true });
    } else if (provider === 'cartesia') {
      setValue('config.tts_cartesia.model_id', modelId, { shouldDirty: true });
      setValue('config.tts_elevenlabs.model_id', '', { shouldDirty: true });
      setValue('config.tts_minimax.model_id', undefined, { shouldDirty: true });
    } else if (provider === 'minimax') {
      setValue('config.tts_minimax.model_id', modelId as 'speech-2.8-hd' | 'speech-2.8-turbo', {
        shouldDirty: true,
      });
      setValue('config.tts_elevenlabs.model_id', '', { shouldDirty: true });
      setValue('config.tts_cartesia.model_id', '', { shouldDirty: true });
    }
  };

  // Auto-switch when the current model isn't viable for the selected language,
  // and pick a default on first load when nothing is selected yet.
  useEffect(() => {
    if (filteredModels.length === 0) {
      return;
    }
    const currentIsViable = filteredModels.some(
      (m) => m.provider === ttsProvider && m.id === currentModelId
    );
    if (currentIsViable) {
      return;
    }
    // Prefer a model from the already-selected provider so we don't yank the user
    // to a different provider just because no model id is set yet (e.g. a stored
    // config whose active provider has no model_id). Only fall back to the first
    // model overall when the current provider has no viable models (or none is set).
    const sameProviderModel = filteredModels.find((m) => m.provider === ttsProvider);
    const next = sameProviderModel ?? filteredModels[0];
    applyModel(next.id, next.provider);
  }, [filteredModels, ttsProvider, currentModelId, setValue]);

  if (isLoading) {
    return <CardSkeleton title={t('integrations.voice_common.voice.text_to_speech')} rows={2} />;
  }

  return (
    <CardBase title={t('integrations.voice_common.voice.text_to_speech')}>
      <TTSModelSelector value={currentModelId} onChange={applyModel} models={filteredModels} />

      {ttsProvider === 'cartesia' && <CartesiaVoice />}
      {ttsProvider === 'elevenlabs' && <ElevenLabsVoice />}
      {ttsProvider === 'minimax' && <MiniMaxVoice />}
    </CardBase>
  );
};

export default Voice;
